import type { TipoCampo } from '../../api/tipos';
import { SERIE } from '../../lib/personas';
import { TIPOS_CAMPO, nuevaClaveLocal } from './constantes';

export interface OpcionEditable {
  id: string | null;
  claveLocal: string;
  texto: string;
}

export interface PreguntaEditable {
  id: string | null;
  claveLocal: string;
  etiqueta: string;
  tipo: TipoCampo;
  condicional: boolean;
  privada: boolean;
  opciones: OpcionEditable[];
  reiniciarVotos: boolean;
  votos: number;
}

export const preguntaNueva = (tipo: TipoCampo): PreguntaEditable => ({
  id: null,
  claveLocal: nuevaClaveLocal(),
  etiqueta: '',
  tipo,
  condicional: true,
  privada: tipo === 'CHECK',
  opciones: [
    { id: null, claveLocal: nuevaClaveLocal(), texto: '' },
    { id: null, claveLocal: nuevaClaveLocal(), texto: '' },
  ],
  reiniciarVotos: false,
  votos: 0,
});

/// El color activo se aplica por style inline, porque lo define el propio chip.
const chip = (activo: boolean) =>
  `rounded-[7px] px-3 py-1.5 text-xs font-semibold transition-colors ${
    activo
      ? 'text-tinta'
      : 'border border-white/15 bg-transparent text-tenue hover:border-white/30'
  }`;

const iconoBtn =
  'h-[34px] w-8 flex-none rounded-lg border border-white/15 text-xs text-tenue transition-colors hover:bg-white/5 disabled:opacity-30';

interface Props {
  preguntas: PreguntaEditable[];
  onCambio: (preguntas: PreguntaEditable[]) => void;
}

export function EditorPreguntas({ preguntas, onCambio }: Props) {
  const parchear = (indice: number, cambios: Partial<PreguntaEditable>) => {
    onCambio(preguntas.map((p, i) => (i === indice ? { ...p, ...cambios } : p)));
  };

  const mover = (indice: number, salto: -1 | 1) => {
    const destino = indice + salto;
    if (destino < 0 || destino >= preguntas.length) return;
    const copia = [...preguntas];
    const [movida] = copia.splice(indice, 1);
    if (movida) copia.splice(destino, 0, movida);
    onCambio(copia);
  };

  return (
    <div className="flex max-w-[880px] flex-col gap-3.5">
      {preguntas.length === 0 && (
        <p className="text-[13px] text-mudo">
          Este evento todavía no tiene preguntas. Agregá una con los botones de abajo, o dejalo así
          si solo querés confirmar asistencia.
        </p>
      )}

      {preguntas.map((pregunta, indice) => {
        return (
          <div
            key={pregunta.claveLocal}
            className="rounded-xl border border-white/[.09] bg-white/[.04] p-4"
          >
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="w-6 flex-none font-titulo text-xs font-extrabold text-debil">
                {String(indice + 1).padStart(2, '0')}
              </span>

              <input
                type="text"
                className="campo min-w-[180px] flex-1"
                placeholder="Escribí la pregunta"
                maxLength={300}
                value={pregunta.etiqueta}
                onChange={(evento) => parchear(indice, { etiqueta: evento.target.value })}
              />

              <select
                className="campo w-[172px] flex-none"
                value={pregunta.tipo}
                onChange={(evento) =>
                  parchear(indice, { tipo: evento.target.value as TipoCampo })
                }
              >
                {TIPOS_CAMPO.map((tipo) => (
                  <option key={tipo.valor} value={tipo.valor}>
                    {tipo.etiqueta}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className={iconoBtn}
                disabled={indice === 0}
                onClick={() => mover(indice, -1)}
                aria-label="Subir"
              >
                ↑
              </button>
              <button
                type="button"
                className={iconoBtn}
                disabled={indice === preguntas.length - 1}
                onClick={() => mover(indice, 1)}
                aria-label="Bajar"
              >
                ↓
              </button>
              <button
                type="button"
                aria-label="Eliminar pregunta"
                className="h-[34px] w-8 flex-none rounded-lg border border-coral/30 text-[13px] text-coral transition-colors hover:bg-coral/10"
                onClick={() => onCambio(preguntas.filter((_, i) => i !== indice))}
              >
                ×
              </button>
            </div>

            <div className="ml-8 mt-3 flex flex-col gap-2">
              {pregunta.opciones.map((opcion, posicion) => (
                <div key={opcion.claveLocal} className="flex items-center gap-2">
                  <span
                    className="h-[7px] w-[7px] flex-none rounded-full"
                    style={{ background: SERIE[posicion % SERIE.length] }}
                  />
                  <input
                    type="text"
                    className="campo max-w-[420px] flex-1 py-2 text-[13px]"
                    placeholder="Opción"
                    maxLength={200}
                    value={opcion.texto}
                    onChange={(evento) =>
                      parchear(indice, {
                        opciones: pregunta.opciones.map((o, i) =>
                          i === posicion ? { ...o, texto: evento.target.value } : o,
                        ),
                      })
                    }
                  />
                  <button
                    type="button"
                    aria-label="Quitar opción"
                    className="px-2 py-1 text-sm text-mudo transition-colors hover:text-coral"
                    onClick={() =>
                      parchear(indice, {
                        opciones: pregunta.opciones.filter((_, i) => i !== posicion),
                      })
                    }
                  >
                    ×
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="mt-0.5 self-start rounded-lg border border-dashed border-white/20 px-3.5 py-1.5 text-[12.5px] text-tenue transition-colors hover:border-white/35"
                onClick={() =>
                  parchear(indice, {
                    opciones: [
                      ...pregunta.opciones,
                      { id: null, claveLocal: nuevaClaveLocal(), texto: '' },
                    ],
                  })
                }
              >
                + Agregar opción
              </button>
            </div>

            <div className="ml-8 mt-3.5 flex flex-wrap items-center gap-3 border-t border-dashed border-white/10 pt-3">
              <button
                type="button"
                className={chip(pregunta.condicional)}
                style={pregunta.condicional ? { background: '#A1C623' } : undefined}
                onClick={() => parchear(indice, { condicional: !pregunta.condicional })}
              >
                {pregunta.condicional ? '✓ ' : ''}Solo si confirma asistencia
              </button>

              <button
                type="button"
                className={chip(pregunta.privada)}
                style={pregunta.privada ? { background: '#29B0E6' } : undefined}
                onClick={() => parchear(indice, { privada: !pregunta.privada })}
              >
                {pregunta.privada ? '✓ ' : ''}Resultados solo para admin
              </button>

              {pregunta.votos > 0 && (
                <div className="ml-auto flex flex-wrap items-center gap-2.5">
                  <span className="text-xs text-ambar">
                    {pregunta.votos === 1
                      ? '1 respuesta ya cargada'
                      : `${pregunta.votos} respuestas ya cargadas`}
                  </span>
                  <button
                    type="button"
                    className={chip(!pregunta.reiniciarVotos)}
                    style={!pregunta.reiniciarVotos ? { background: '#2B90CF' } : undefined}
                    onClick={() => parchear(indice, { reiniciarVotos: false })}
                  >
                    Mantener
                  </button>
                  <button
                    type="button"
                    className={chip(pregunta.reiniciarVotos)}
                    style={pregunta.reiniciarVotos ? { background: '#E8C24A' } : undefined}
                    onClick={() => parchear(indice, { reiniciarVotos: true })}
                  >
                    Resetear
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap gap-2 pt-0.5">
        {TIPOS_CAMPO.map((tipo) => (
          <button
            key={tipo.valor}
            type="button"
            className="rounded-[9px] border border-dashed border-white/20 bg-white/5 px-3.5 py-2 text-[12.5px] text-suave transition-colors hover:border-white/35"
            onClick={() => onCambio([...preguntas, preguntaNueva(tipo.valor)])}
          >
            + {tipo.etiqueta}
          </button>
        ))}
      </div>
    </div>
  );
}
